import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, LegalSection } from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout eyebrow="privacy" title="Your data, in your words." updated="Feb 2026">
      <p data-testid="privacy-intro" className="text-foreground">
        Short version: we collect the minimum needed to make Book Away work,
        we don't sell it, and you can ask us to delete it whenever you want.
      </p>

      <LegalSection title="What we collect">
        <p><strong>If you're a business:</strong> your email, name, business name, and street address (which we geocode into lat/lng so the map shows you in the right spot).</p>
        <p><strong>If you're a consumer:</strong> a phone number and an optional name + short note when you send a booking request. That's it.</p>
        <p><strong>If you use the companion mobile app:</strong> your Expo device token so we can send push notifications, and (optionally) the businesses you choose to follow.</p>
        <p>We do <strong>not</strong> collect: your location in the background, your contacts, your payment details, or anything else you don't type into the app yourself.</p>
      </LegalSection>

      <LegalSection title="How we use it">
        <p><strong>Businesses:</strong> your address becomes a pin on the map so consumers can find you. Your business name shows up next to your openings.</p>
        <p><strong>Consumers:</strong> the business you request sees your phone + name + note so they can confirm. No one else sees it — not us for marketing, not other businesses.</p>
        <p><strong>Notifications:</strong> we send push alerts for things you'd want to know (booking approved, opening filled, followed business posted a new slot). Nothing promotional.</p>
      </LegalSection>

      <LegalSection title="Who else sees your data">
        <p>Nobody, unless we say so here or the law forces us to. Specifically:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Businesses see</strong> the phone, name, and note on requests
            sent to them.
          </li>
          <li>
            <strong>Consumers see</strong> the business's public info (name, address, hours, reviews) — never other consumers' details.
          </li>
          <li>
            <strong>Our service providers</strong> (map tiles, push
            notifications, hosting) process data on our behalf under standard
            confidentiality terms. We don't sell or share data with anyone else.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="How long we keep it">
        <p>
          Booking requests are kept for 12 months so you have a history. Openings that are cancelled or expired are auto-cleared within 24 hours. Your account and reviews stay until you ask us to delete them.
        </p>
      </LegalSection>

      <LegalSection title="Your rights">
        <p>
          You can ask us at any time to:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Show you what data we hold about you.</li>
          <li>Correct anything that's wrong.</li>
          <li>Delete your account and all associated data.</li>
          <li>Export your data (openings, requests, reviews) as JSON.</li>
        </ul>
        <p>
          Email{" "}
          <a
            href="mailto:hello@bookaway.com.au"
            className="underline hover:text-foreground"
          >
            hello@bookaway.com.au
          </a>{" "}
          and we'll act on it within 7 days.
        </p>
      </LegalSection>

      <LegalSection title="Cookies + local storage">
        <p>
          We use a login cookie to keep you signed in, and browser local
          storage to remember tiny UI preferences (like "you dismissed the
          Install App banner"). No tracking cookies, no third-party ad pixels.
        </p>
      </LegalSection>

      <LegalSection title="Security">
        <p>
          Passwords are hashed with bcrypt — we never see them in plain text.
          Data in transit is HTTPS-encrypted. We store data in
          professionally-managed cloud infrastructure with standard access
          controls.
        </p>
        <p>
          No system is 100% secure. If we ever discover a breach affecting you,
          we'll tell you within 72 hours.
        </p>
      </LegalSection>

      <LegalSection title="Kids">
        <p>
          Book Away is for people 16 and older. If you're a parent and think
          your child made an account, email us and we'll delete it.
        </p>
      </LegalSection>

      <LegalSection title="Changes">
        <p>
          If we change this policy, we'll bump the "Last updated" date and
          notify you on your dashboard before the change takes effect. For
          material changes (like sharing data with new third parties), we'll
          ask for your consent — not just assume it.
        </p>
      </LegalSection>

      <p className="mono text-[11px] uppercase tracking-widest text-muted-foreground pt-4 border-t border-border">
        Read the{" "}
        <Link to="/terms" className="text-foreground hover:text-[#C9A85F]">
          terms of use
        </Link>{" "}
        · Contact →{" "}
        <a
          href="mailto:hello@bookaway.com.au"
          className="text-foreground hover:text-[#C9A85F]"
        >
          hello@bookaway.com.au
        </a>
      </p>
    </LegalLayout>
  );
}
